import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { UnifiedAccount, UnifiedInstitution } from '../../types/Account.interface';

import useStepAwarePlaidItems from 'services/hurdlr/queries/useStepAwarePlaidItems';

// Error types for better error handling
interface ValidationError extends Error {
    type: 'validation' | 'data' | 'network' | 'unknown';
    details?: string[];
}

// Validation result interface
interface ValidationResult {
    isValid: boolean;
    errors: string[];
    warnings: string[];
}

interface UseUnifiedAccountsProps {
    memberId?: number;
    onboardingProgress?: {
        transaction_import_data?: {
            source_accounts?: Array<{
                plaidAccountId?: string;
            }>;
        };
        bank_connection_data?: {
            confirmed_personal_accounts?: Array<{
                id: string;
            }>;
        };
    };
    newlyConnectedAccounts?: Array<{
        plaidAccountId?: string;
        id?: string;
        name: string;
        type: string;
        lastFour: string;
        institution: string;
        status: string;
    }>;
}

/**
 * Balanced validation functions - Critical vs Non-Critical
 *
 * Critical validation: Must fail fast to prevent runtime errors
 * Non-Critical validation: Log warnings but continue processing
 */

// Critical validation - will cause runtime errors if failed
const validateCriticalInputs = (props: UseUnifiedAccountsProps): ValidationResult => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Critical: memberId must be valid if provided
    if (props.memberId !== undefined && (typeof props.memberId !== 'number' || props.memberId <= 0)) {
        errors.push('Invalid memberId: must be a positive number');
    }

    // Critical: newlyConnectedAccounts must have valid structure if provided
    if (props.newlyConnectedAccounts) {
        const invalidAccounts = props.newlyConnectedAccounts.filter(
            (account) => !account.id && !account.plaidAccountId // At least one ID must exist
        );
        if (invalidAccounts.length > 0) {
            errors.push('Newly connected accounts missing required IDs');
        }
    }

    return { isValid: errors.length === 0, errors, warnings };
};

// Non-critical validation - log warnings but continue
const validateAccountData = (account: any, source: string): { account: UnifiedAccount | null; warnings: string[] } => {
    const warnings: string[] = [];

    // Critical: Must have account ID
    if (!account.apiAccountId || typeof account.apiAccountId !== 'string') {
        console.warn(`Invalid account from ${source}: missing apiAccountId`);
        warnings.push(`Invalid account from ${source}: missing apiAccountId`);
        return { account: null, warnings };
    }

    // Non-critical: Log warnings for missing optional fields
    if (!account.apiAccountName || typeof account.apiAccountName !== 'string') {
        warnings.push(`Account ${account.apiAccountId} missing name`);
    }

    if (!account.apiAccountNo || typeof account.apiAccountNo !== 'string') {
        warnings.push(`Account ${account.apiAccountId} missing account number`);
    }

    if (!account.apiAccountType || typeof account.apiAccountType !== 'string') {
        warnings.push(`Account ${account.apiAccountId} missing account type`);
    }

    if (!account.status || typeof account.status !== 'string') {
        warnings.push(`Account ${account.apiAccountId} missing status`);
    }

    // Create account with fallbacks for missing data
    const unifiedAccount: UnifiedAccount = {
        id: account.apiAccountId,
        hurdlrBankAccountId: account.id,
        name: account.apiAccountName || 'Unknown Account',
        type: account.apiAccountType || 'UNKNOWN',
        subtype: account.apiSubType,
        lastFour: account.apiAccountNo || '****',
        institution: account.apiInstitutionName || 'Unknown Institution',
        institutionId: account.apiInstitutionId, // Preserve institution ID for logo fetching
        status: account.status || 'UNKNOWN',
        plaidItemId: account.plaidItemId,
        isFromBackend: true,
        isFromBizAccountSetup: false,
    };

    return { account: unifiedAccount, warnings };
};

// Graceful duplicate resolution - prefer backend data but don't fail
const resolveDuplicateAccounts = (accounts: UnifiedAccount[]): UnifiedAccount[] => {
    const accountMap = new Map<string, UnifiedAccount>();
    const warnings: string[] = [];

    accounts.forEach((account) => {
        const existingAccount = accountMap.get(account.id);

        if (existingAccount) {
            // Prefer backend data over newly connected
            if (account.isFromBackend && !existingAccount.isFromBackend) {
                accountMap.set(account.id, account);
                warnings.push(`Resolved duplicate account ${account.id}: preferred backend data`);
            } else if (!account.isFromBackend && existingAccount.isFromBackend) {
                // Keep existing backend data
                warnings.push(`Resolved duplicate account ${account.id}: kept backend data`);
            } else {
                // Same source, prefer more complete data
                const accountCompleteness = Object.values(account).filter(Boolean).length;
                const existingCompleteness = Object.values(existingAccount).filter(Boolean).length;

                if (accountCompleteness > existingCompleteness) {
                    accountMap.set(account.id, account);
                    warnings.push(`Resolved duplicate account ${account.id}: preferred more complete data`);
                }
            }
        } else {
            accountMap.set(account.id, account);
        }
    });

    if (warnings.length > 0) {
        console.warn('Duplicate account resolution warnings:', warnings);
    }

    return Array.from(accountMap.values());
};

/**
 * Unified Accounts Hook
 *
 * This hook combines the functionality of useFilteredAccounts and useImportAccounts
 * into a single, streamlined hook that provides all account data in a consistent format.
 *
 * Replaces:
 * - useFilteredAccounts (190 lines)
 * - useImportAccounts (192 lines)
 *
 * Provides:
 * - Single data transformation path
 * - Consistent UnifiedAccount[] output
 * - Simplified data flow
 * - Balanced validation (critical vs non-critical)
 */
function useUnifiedAccounts({ memberId, onboardingProgress, newlyConnectedAccounts = [] }: UseUnifiedAccountsProps) {
    const queryClient = useQueryClient();

    // Critical validation - fail fast if inputs are invalid
    const criticalValidation = useMemo(() => {
        return validateCriticalInputs({ memberId, onboardingProgress, newlyConnectedAccounts });
    }, [memberId, onboardingProgress, newlyConnectedAccounts]);

    const stepAwareParams = {
        memberId,
        include_bank_accounts: true,
        step: 'transaction_import',
    };

    // Fetch all plaid items and accounts from backend
    const {
        data: stepAwareData,
        isLoading: stepAwareLoading,
        error,
        refetch,
    } = useStepAwarePlaidItems(stepAwareParams);

    // Check if ALL required data is ready to prevent flickering
    const isAllDataReady = useMemo(() => {
        const ready =
            stepAwareData && // Backend account data is loaded
            onboardingProgress && // Onboarding progress is loaded (passed as prop)
            !stepAwareLoading; // No queries are still loading

        return ready;
    }, [stepAwareData, onboardingProgress, stepAwareLoading]);

    // Transform and filter all accounts into unified format with balanced validation
    const { unifiedAccounts, validationWarnings } = useMemo(() => {
        // Return empty state if critical validation fails
        if (!criticalValidation.isValid) {
            return { unifiedAccounts: [], validationWarnings: criticalValidation.warnings };
        }

        // Return empty state while waiting for all data to prevent flickering
        if (!isAllDataReady) {
            return { unifiedAccounts: [], validationWarnings: [] };
        }

        const allWarnings: string[] = [];

        // Extract Plaid account IDs from the 3 filter criteria
        const transactionImportIds =
            onboardingProgress?.transaction_import_data?.source_accounts
                ?.map((account) => account.plaidAccountId)
                .filter(Boolean) || [];

        const bankConnectionIds =
            onboardingProgress?.bank_connection_data?.confirmed_personal_accounts
                ?.map((account) => account.id)
                .filter(Boolean) || [];

        const newlyConnectedIds =
            newlyConnectedAccounts?.map((account) => account.plaidAccountId || account.id).filter(Boolean) || [];

        // Combine all filter criteria IDs
        const allFilterIds = [...transactionImportIds, ...bankConnectionIds, ...newlyConnectedIds];

        const accounts: UnifiedAccount[] = [];

        // 1. Transform backend accounts from stepAwareData with validation
        if (stepAwareData?.accounts) {
            stepAwareData.accounts.forEach((plaidItem) => {
                // Non-critical: Log warning if institution name is missing but continue
                if (!plaidItem.apiInstitutionName) {
                    allWarnings.push(`PlaidItem missing institution name: ${plaidItem.id || 'unknown'}`);
                }

                plaidItem.bank_accounts.forEach((backendAccount) => {
                    const accountPlaidId = backendAccount.apiAccountId;

                    // Only include accounts that match our filter criteria
                    if (allFilterIds.includes(accountPlaidId)) {
                        const { account, warnings } = validateAccountData(backendAccount, 'backend');

                        if (account) {
                            // Set institution from plaidItem (with fallback)
                            account.institution = plaidItem.apiInstitutionName || 'Unknown Institution';
                            account.institutionId = plaidItem.apiInstitutionId; // Preserve institution ID for logo fetching
                            account.plaidItemId = plaidItem.id;
                            accounts.push(account);
                        }

                        allWarnings.push(...warnings);
                    }
                });
            });
        }

        // 2. Add newly connected accounts that don't exist in backend yet
        newlyConnectedAccounts.forEach((newAccount) => {
            const newAccountPlaidId = newAccount.plaidAccountId || newAccount.id;
            if (!newAccountPlaidId) {
                allWarnings.push('Newly connected account missing ID');
                return;
            }

            // Check if this account already exists in backend data
            const existsInBackend = accounts.some((account) => account.id === newAccountPlaidId);

            if (!existsInBackend) {
                // Non-critical: Use fallbacks for missing data but continue
                accounts.push({
                    id: newAccountPlaidId,
                    hurdlrBankAccountId: undefined, // Will be set when backend syncs
                    name: newAccount.name || 'Unknown Account',
                    type: newAccount.type || 'UNKNOWN',
                    lastFour: newAccount.lastFour || '****',
                    institution: newAccount.institution || 'Unknown Institution',
                    status: newAccount.status || 'UNKNOWN',
                    plaidItemId: undefined, // Will be set when backend syncs
                    isFromBackend: false,
                    isFromBizAccountSetup: false,
                });
            }
        });

        // 3. Resolve duplicates gracefully
        const resolvedAccounts = resolveDuplicateAccounts(accounts);

        return {
            unifiedAccounts: resolvedAccounts,
            validationWarnings: [...criticalValidation.warnings, ...allWarnings],
        };
    }, [
        isAllDataReady,
        stepAwareData,
        onboardingProgress,
        newlyConnectedAccounts,
        criticalValidation.warnings,
        criticalValidation.isValid,
    ]);

    // Group accounts by institution for UI consumption
    const institutions = useMemo(() => {
        const grouped = unifiedAccounts.reduce((acc, account) => {
            const institutionName = account.institution;

            if (!acc[institutionName]) {
                acc[institutionName] = [];
            }

            acc[institutionName].push(account);
            return acc;
        }, {} as Record<string, UnifiedAccount[]>);

        // Convert to UnifiedInstitution array
        return Object.entries(grouped).map(([name, accounts]) => ({
            name,
            accounts,
        })) as UnifiedInstitution[];
    }, [unifiedAccounts]);

    // Get all unique institution names
    const institutionNames = useMemo(() => {
        return institutions.map((inst) => inst.name);
    }, [institutions]);

    // Get total count of accounts
    const totalAccountCount = useMemo(() => {
        return unifiedAccounts.length;
    }, [unifiedAccounts]);

    // Helper functions
    const getAccountsByInstitution = (institutionName: string): UnifiedAccount[] => {
        const institution = institutions.find((inst) => inst.name === institutionName);
        return institution?.accounts || [];
    };

    const getAccountById = (id: string): UnifiedAccount | undefined => {
        return unifiedAccounts.find((account) => account.id === id);
    };

    const getAccountByHurdlrId = (hurdlrId: number): UnifiedAccount | undefined => {
        return unifiedAccounts.find((account) => account.hurdlrBankAccountId === hurdlrId);
    };

    // Query invalidation functions
    const invalidatePlaidItems = async () => {
        await queryClient.invalidateQueries({ queryKey: ['step-aware-plaid-items'] });
    };

    const invalidateOnboardingProgress = async () => {
        await queryClient.invalidateQueries({ queryKey: ['accounting-onboarding-progress'] });
    };

    const invalidateQueries = async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['step-aware-plaid-items'] }),
            queryClient.invalidateQueries({ queryKey: ['accounting-onboarding-progress'] }),
        ]);
    };

    // Prepare step data for the StepWrapper (backward compatibility)
    const stepData = useMemo(() => {
        return {
            source_accounts: unifiedAccounts
                .filter((account) => account.isFromBackend || account.isFromBizAccountSetup)
                .map((account) => ({
                    id: account.id,
                    name: account.name,
                    type: account.type,
                    lastFour: account.lastFour,
                    institution: account.institution,
                    status: account.status,
                    plaidAccountId: account.id,
                })),
        };
    }, [unifiedAccounts]);

    // If critical validation fails, return safe fallback data
    if (!criticalValidation.isValid) {
        const validationError: ValidationError = new Error(
            `Critical validation failed: ${criticalValidation.errors.join(', ')}`
        ) as ValidationError;
        validationError.type = 'validation';
        validationError.details = criticalValidation.errors;

        return {
            unifiedAccounts: [],
            institutions: [],
            institutionNames: [],
            stepData: { source_accounts: [] },
            totalAccountCount: 0,
            isLoading: stepAwareLoading,
            error: validationError,
            refetch,
            invalidateQueries: async () => {
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ['step-aware-plaid-items'] }),
                    queryClient.invalidateQueries({ queryKey: ['accounting-onboarding-progress'] }),
                ]);
            },
            invalidatePlaidItems: async () => {
                await queryClient.invalidateQueries({ queryKey: ['step-aware-plaid-items'] });
            },
            invalidateOnboardingProgress: async () => {
                await queryClient.invalidateQueries({ queryKey: ['accounting-onboarding-progress'] });
            },
            getAccountsByInstitution: () => [],
            getAccountById: () => undefined,
            getAccountByHurdlrId: () => undefined,
            validationWarnings: criticalValidation.warnings,
        };
    }

    return {
        // Data
        unifiedAccounts,
        institutions,
        institutionNames,
        stepData,

        // Metadata
        totalAccountCount,

        // State
        isLoading: stepAwareLoading,
        error,

        // Actions
        refetch,
        invalidateQueries,
        invalidatePlaidItems,
        invalidateOnboardingProgress,

        // Helper functions
        getAccountsByInstitution,
        getAccountById,
        getAccountByHurdlrId,

        // Validation
        validationWarnings,
    };
}

export default useUnifiedAccounts;
